import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';

/**
 * Detects if a click occured outside of the passed ref DOM node.
 *
 * Note: If the passed in handler function is a new reference on every render, it will cause this effect to do
 * the cleanup and setup logic on every render. To optimize the performance you can wrap the handler function in
 * `useCallback` before passing it into this hook.
 *
 * @param ref the reference to the DOM node which serves as boundary for the outside click calculations.
 * @param handler a handler that is called when an outside click occurs.
 * @param active set to `true` to activate the outside click handling monitoring
 */
export function useOnClickOutside(handler, active, ignoredElements) {
  const $ = _c(5);
  const ref = useRef(null);
  let t0;
  let t1;
  if ($[0] !== active || $[1] !== handler || $[2] !== ignoredElements) {
    t0 = () => {
      if (active) {
        const listener = function listener(event) {
          const element = ref.current;
          if (!element || element.contains(event.target as Node) || ignoredElements.some(element_0 => element_0 && element_0.contains(event.target as Node))) {
            return;
          }
          handler(event);
        };
        document.addEventListener("mousedown", listener, {
          capture: true
        });
        document.addEventListener("touchstart", listener, {
          capture: true
        });
        return () => {
          document.removeEventListener("mousedown", listener, {
            capture: true
          });
          document.removeEventListener("touchstart", listener, {
            capture: true
          });
        };
      } else {
        return;
      }
    };
    t1 = [ref, handler, active, ignoredElements];
    $[0] = active;
    $[1] = handler;
    $[2] = ignoredElements;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return ref;
}